import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import type { Site } from '../../../lib/api';
import type { SupplierFormData } from './types';

interface WizardStep2RiskRoutingProps {
  formData: SupplierFormData;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[]) => void;
  previewMode: boolean;
  /** The customer sites actually linked to this supplier — the only routing targets. */
  linkedSites: Site[];
  /** `[groupId, groupName]` pairs derived from those linked sites. */
  groupOptions: [number, string][];
}

/**
 * CR-5877: Risk Question Routing Management.
 *
 * The buyer explicitly chooses how risk questionnaires reach the supplier —
 * it is no longer inferred from the order customer companies were selected in.
 *   Group → the entire customer group
 *   Site  → one specific site, or every site within a group
 *
 * CR-7774: extracted from WizardStep2Details, which had grown past the 500-line
 * cap. Behaviour is unchanged — this is the same markup with the values it
 * needs passed in.
 */
export default function WizardStep2RiskRouting({
  formData,
  onInputChange,
  previewMode,
  linkedSites,
  groupOptions,
}: WizardStep2RiskRoutingProps) {
  const routingLevel = formData.risk_routing_level ?? null;
  const routingScope = formData.risk_routing_site_scope ?? null;

  const groupPlaceholder = groupOptions.length === 0
    ? 'No groups available for selected companies'
    : 'Select customer group...';

  return (
    <div className="pt-4 border-t space-y-4">
      <div>
        <h3 className="text-lg font-medium">Risk Question Routing Management <span className="text-red-500">*</span></h3>
        <p className="text-sm text-muted-foreground">How do you want to manage risk question routing?</p>
      </div>

      <RadioGroup
        value={routingLevel ?? ''}
        onValueChange={(v) => {
          const next = (v === 'group' || v === 'site') ? v : null;
          onInputChange('risk_routing_level', next);
          // Reset dependent fields when switching level so stale state
          // from the other branch doesn't leak through validation.
          if (next === 'group') {
            onInputChange('risk_routing_site_scope', null);
            onInputChange('risk_routing_site_id', null);
          }
          // For 'site', keep risk_routing_group_id — site routing also
          // tracks group context.
        }}
        disabled={previewMode}
        className="gap-3"
      >
        <div className="flex items-start gap-3 p-3 border rounded-lg">
          <RadioGroupItem value="group" id="routing-group" className="mt-1" />
          <div className="flex-1 space-y-2">
            <Label htmlFor="routing-group" className="font-medium cursor-pointer">
              Group Level Routing
            </Label>
            <p className="text-xs text-muted-foreground">
              Route risk questionnaires to the entire customer group.
            </p>
            {routingLevel === 'group' && (
              <Select
                value={formData.risk_routing_group_id ? String(formData.risk_routing_group_id) : ''}
                onValueChange={(v) => onInputChange('risk_routing_group_id', Number(v))}
                disabled={previewMode}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder={groupPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {groupOptions.map(([gid, gname]) => (
                    <SelectItem key={gid} value={String(gid)}>{gname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 border rounded-lg">
          <RadioGroupItem value="site" id="routing-site" className="mt-1" />
          <div className="flex-1 space-y-2">
            <Label htmlFor="routing-site" className="font-medium cursor-pointer">
              Site Level Routing
            </Label>
            <p className="text-xs text-muted-foreground">
              Target a specific site, or distribute across all sites within a group.
            </p>
            {routingLevel === 'site' && (
              <div className="space-y-2">
                <RadioGroup
                  value={routingScope ?? ''}
                  onValueChange={(v) => {
                    const next = (v === 'single' || v === 'all') ? v : null;
                    onInputChange('risk_routing_site_scope', next);
                    if (next === 'single') {
                      onInputChange('risk_routing_group_id', null);
                    } else if (next === 'all') {
                      onInputChange('risk_routing_site_id', null);
                    }
                  }}
                  disabled={previewMode}
                  className="gap-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="single" id="routing-site-single" />
                    <Label htmlFor="routing-site-single" className="cursor-pointer text-sm">Single site</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="all" id="routing-site-all" />
                    <Label htmlFor="routing-site-all" className="cursor-pointer text-sm">All sites within group</Label>
                  </div>
                </RadioGroup>

                {routingScope === 'single' && (
                  <Select
                    value={formData.risk_routing_site_id ? String(formData.risk_routing_site_id) : ''}
                    onValueChange={(v) => onInputChange('risk_routing_site_id', Number(v))}
                    disabled={previewMode}
                  >
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder="Select site..." />
                    </SelectTrigger>
                    <SelectContent>
                      {linkedSites.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                          {s.group_name ? ` — ${s.group_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {routingScope === 'all' && (
                  <Select
                    value={formData.risk_routing_group_id ? String(formData.risk_routing_group_id) : ''}
                    onValueChange={(v) => onInputChange('risk_routing_group_id', Number(v))}
                    disabled={previewMode}
                  >
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder={groupPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {groupOptions.map(([gid, gname]) => (
                        <SelectItem key={gid} value={String(gid)}>{gname}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
